import React, { useContext, useEffect, useState, useRef } from 'react';
import styled from 'styled-components';
import { GroupWatchingSettings, Participant } from 'components';
import { FirebaseContext } from 'context';
import { AnimatePresence, motion } from 'framer-motion';

const onlyShowTheseRolesAndInThisOrder = ['isOrganiser', 'isModerator', 'isSpeaker'];

function Participants({
  colors,
  eventId,
  showOnlinePresenceToUsers,
  handleParticipantAvatarOrNameClick
}) {
  const { firebase, user } = useContext(FirebaseContext);
  const [usersCurrentlyWatchingEvent, setUsersCurrentlyWatchingEvent] = useState(null);
  const [groupWatchingAmount, setGroupWatchingAmount] = useState(0);

  useEffect(() => {
    if (firebase) {
      firebase.interaction.participants.getEventParticipantsOnlineCount({
        eventId,
        callback: (snapshot) => {
          if (!snapshot.empty) {
            const { count } = snapshot.data();
            setUsersCurrentlyWatchingEvent(count);
          }
        }
      });
    }
    return () => {
      setUsersCurrentlyWatchingEvent(null);
    };
  }, [firebase, eventId]);

  useEffect(() => {
    if (user?.groupWatching?.[eventId]?.length) {
      setGroupWatchingAmount(user.groupWatching[eventId]?.length);
    } else {
      setGroupWatchingAmount(0);
    }
  }, [user?.groupWatching]);

  const particpantsContainerRef = useRef();

  const [lastFetchedCurrentlyParticipatingUserDoc, setLastFetchedCurrentlyParticipatingUserDoc] =
    useState();

  const [
    lastFetchedNotCurrentlyParticipatingUserDoc,
    setLastFetchedNotCurrentlyParticipatingUserDoc
  ] = useState();

  // 'participants' includes anyone who has registered for the event, whether they are
  // currently on the event livestream page or not. If they are currently on the page they'll have a
  // green <AvailabilityIndicator /> by their avatar. If they've registered for this event but are
  // offline or are currently on another event livestream page then they'll still be listed as a
  // participant for this event, just without the <AvailabilityIndicator /> by their avatar.
  // If not for the logical OR logic in fetchParticipantsWithRoles() then users would not be
  // listed as non-available participants on event pages if watching a concurrent livestream on
  // another event page.
  const [participants, setParticpants] = useState([]);

  const populatePaginatedParticipantsList = async (snapshots, { initialFetch = false } = {}) => {
    const nextParticipants = [];

    const [currentlyParticipatingUsersSnapshot, notCurrentlyParticipatingUsersSnapshot] = snapshots;

    // On the initial fetch after an eventId change the 'participants' value this closure
    // captured still holds the previous stage's list, so don't dedupe against it.
    const alreadyListedParticipants = initialFetch ? [] : participants;

    const userHasNotBeenAddedToTheParticipantsListAlready = (uid) =>
      !alreadyListedParticipants.some((participant) => participant.uid === uid);

    if (!currentlyParticipatingUsersSnapshot?.empty) {
      currentlyParticipatingUsersSnapshot.docs.forEach((doc) => {
        const { uid } = doc.data();
        if (userHasNotBeenAddedToTheParticipantsListAlready(uid)) {
          nextParticipants.push(doc);
        }
      });
    }

    if (
      currentlyParticipatingUsersSnapshot?.empty &&
      !notCurrentlyParticipatingUsersSnapshot?.empty
    ) {
      notCurrentlyParticipatingUsersSnapshot.docs.forEach((doc) => {
        const { uid } = doc.data();
        if (userHasNotBeenAddedToTheParticipantsListAlready(uid)) {
          nextParticipants.push(doc);
        }
      });
    }

    if (nextParticipants.length) {
      setParticpants((currrentParticipants) => [
        ...(initialFetch ? [] : currrentParticipants),
        ...nextParticipants.map((doc) => {
          const {
            name,
            profession,
            company,
            socials,
            avatarUrl,
            roles,
            uid,
            presence,
            groupWatching
          } = doc.data();
          return {
            name,
            profession,
            company,
            socials,
            avatarUrl,
            roles,
            uid,
            presence,
            groupWatching
          };
        })
      ]);

      if (!currentlyParticipatingUsersSnapshot?.empty) {
        setLastFetchedCurrentlyParticipatingUserDoc(
          currentlyParticipatingUsersSnapshot.docs[
            currentlyParticipatingUsersSnapshot.docs.length - 1
          ]
        );
      }

      if (
        currentlyParticipatingUsersSnapshot?.empty &&
        !notCurrentlyParticipatingUsersSnapshot?.empty
      ) {
        setLastFetchedNotCurrentlyParticipatingUserDoc(
          notCurrentlyParticipatingUsersSnapshot.docs[
            notCurrentlyParticipatingUsersSnapshot.docs.length - 1
          ]
        );
      }
    }
  };

  // This fetches normal participants, i.e. attendees without 'Organiser', 'Moderator', or 'Speaker'
  // roles. There could be hundreds of them, so we paginate the fetch requests to keep database
  // queries to a minimum.
  // initialFetch matters when the eventId changes (multi-stage tab switch): this closure was
  // created before the cleanup reset the pagination cursors, so the captured cursor state still
  // points at the previous stage's docs and must be ignored for the new stage's first page.
  const fetchPaginatedParticipants = ({ initialFetch = false } = {}) =>
    firebase.interaction.participants
      .fetchPaginatedParticipants({
        eventId,
        lastFetchedCurrentlyParticipatingUserDoc: initialFetch
          ? null
          : lastFetchedCurrentlyParticipatingUserDoc,
        lastFetchedNotCurrentlyParticipatingUserDoc: initialFetch
          ? null
          : lastFetchedNotCurrentlyParticipatingUserDoc
      })
      .then((snapshots) => populatePaginatedParticipantsList(snapshots, { initialFetch }));

  const populateParticipantsWithRolesList = (snapshots) => {
    const participantsWithRoles = [];

    snapshots.forEach((snapshot) => {
      if (!snapshot?.empty) {
        snapshot.docs.forEach((doc) => {
          const { name, profession, company, socials, avatarUrl, roles, uid, presence } =
            doc.data();

          const _participant = {
            name,
            profession,
            company,
            socials,
            avatarUrl,
            roles,
            uid,
            presence
          };

          let rolesString;

          const unsortedRoles = Object.entries(_participant.roles).reduce((acc, entry) => {
            const [eventRole, eventIds] = entry;
            // We're only interested in the 'isOrganiser', 'isModerator', and 'isSpeaker' entries.
            if (
              onlyShowTheseRolesAndInThisOrder.includes(eventRole) &&
              eventIds.includes(eventId)
            ) {
              acc.push(eventRole);
            }
            return acc;
          }, []);

          if (unsortedRoles.length) {
            // If the participant has multiple roles, they'll be sorted in order of importance,
            // as specified in the onlyShowTheseRolesAndInThisOrder array.
            const sortedAndFormattedRoles = unsortedRoles
              .sort(
                (a, b) =>
                  onlyShowTheseRolesAndInThisOrder.indexOf(a) -
                  onlyShowTheseRolesAndInThisOrder.indexOf(b)
              )
              .map((eventRole) => eventRole.slice(2)); // Removes the 'is' part of the role.

            rolesString = sortedAndFormattedRoles.join(', ');
          }

          if (
            rolesString &&
            !participantsWithRoles.some((participant) => participant.uid === uid)
          ) {
            participantsWithRoles.push({
              ..._participant,
              rolesString
            });
          }
        });
      }
    });

    // If there are any participantsWithRoles then as soon as the last one appears in view
    // the useIntersection will call fetchPaginatedParticipants(). If there are no
    // participantsWithRoles then we'll just immediately call fetchPaginatedParticipants().
    if (participantsWithRoles.length) {
      const participantsWithRolesSortedByOnlinePresence = participantsWithRoles.sort(
        (a, b) =>
          (b.presence.selectedEventId === eventId) - (a.presence.selectedEventId === eventId)
      );
      setParticpants(participantsWithRolesSortedByOnlinePresence);
    } else {
      fetchPaginatedParticipants({ initialFetch: true });
    }
  };

  useEffect(() => {
    firebase.interaction.participants
      .fetchParticipantsWithRoles(eventId)
      .then(populateParticipantsWithRolesList);
    return () => {
      setParticpants([]);
      setLastFetchedCurrentlyParticipatingUserDoc(null);
      setLastFetchedNotCurrentlyParticipatingUserDoc(null);
    };
  }, [eventId]);

  // Logs any users that have been added more than once to the participants list.
  // Ideally we'll never need this.
  const duplicateUsers = participants
    .map(({ uid }) => uid)
    .filter((_uid, index) => index !== participants.map(({ uid }) => uid).indexOf(_uid));

  if (duplicateUsers.length) {
    console.warn('Duplicate users: ', duplicateUsers);
  }

  return (
    <Wrapper ref={particpantsContainerRef} colors={colors}>
      <AnimatePresence>
        {groupWatchingAmount && (
          <GroupWatchingSettings colors={colors} amount={groupWatchingAmount} />
        )}
      </AnimatePresence>
      {user.isAdmin && (
        <Info colors={colors}>
          <p>Online Participants: {usersCurrentlyWatchingEvent}</p>
        </Info>
      )}
      {participants.length > 0 &&
        participants.map((participant, i, arr) => (
          <Participant
            key={participant.uid}
            eventId={eventId}
            participant={participant}
            showOnlinePresenceToUsers={user.isAdmin || showOnlinePresenceToUsers}
            fetchPaginatedParticipants={fetchPaginatedParticipants}
            handleParticipantAvatarOrNameClick={handleParticipantAvatarOrNameClick}
            isLastFetchedParticipant={arr.length === i + 1}
            colors={colors}
            particpantsContainerRef={particpantsContainerRef}
          />
        ))}
    </Wrapper>
  );
}

const Wrapper = styled(motion.div).attrs({
  initial: { opacity: 0 },
  animate: { opacity: 1 },
  exit: { opacity: 0 }
})`
  background-color: #fff;
  padding: 1.25rem;
  min-height: 300px;
  overflow-x: hidden;
  overflow-y: auto;
  ::-webkit-scrollbar {
    width: 0.5rem;
  }
  ::-webkit-scrollbar-track {
    box-shadow: inset 0 0 0.31rem grey;
    border-radius: 0.625rem;
  }
  ::-webkit-scrollbar-thumb {
    background-color: ${({ colors }) => colors.secondary};
    border-radius: 0.625rem;
  }
`;

const Info = styled.div`
  padding-bottom: 0.5rem;
  p {
    color: ${({ colors }) => colors.secondary};
    font-size: 0.75rem;
    text-align: right;
    text-transform: uppercase;
  }
`;

export default Participants;
